export interface DiagnosticEvent {
  at: string;
  type: "console" | "javascript" | "promise" | "api";
  message: string;
  detail?: string;
}

export interface DiagnosticBusinessEntity {
  type: "ASN" | "REC" | "HU" | "WAVE" | "PICK" | "TOUR" | "MISSION" |
    "ALLOCATION" | "ORDER" | "LOCATION" | "EQUIPMENT";
  identifier: string;
  status?: string | null;
}

export interface DiagnosticBusinessContext {
  screen: string;
  action?: string | null;
  entities: DiagnosticBusinessEntity[];
}

export type DiagnosticSeverity = "INFO" | "WARNING" | "ERROR" | "CRITICAL";
export type DiagnosticSource = "MANUAL" | "AUTOMATIC";

export interface AutomaticDiagnostic {
  source: "AUTOMATIC";
  severity: DiagnosticSeverity;
  fingerprint: string;
  businessContext: DiagnosticBusinessContext;
  snapshot: ReturnType<typeof diagnosticSnapshot>;
}

const events: DiagnosticEvent[] = [];
const MAX_EVENTS = 50;
let installed = false;
let fallbackTerminalId: string | undefined;
let businessContext: DiagnosticBusinessContext = {
  screen: "Écran non identifié", entities: []
};
let automaticReporter: ((diagnostic: AutomaticDiagnostic) => Promise<void>) | null = null;
let automaticReportInProgress = false;
const AUTOMATIC_REPORT_INTERVAL_MS = 5 * 60_000;

const redact = (value: unknown) => {
  let text: string;
  try {
    text = value instanceof Error
      ? `${value.name}: ${value.message}\n${value.stack ?? ""}`
      : typeof value === "string" ? value : JSON.stringify(value);
  } catch {
    text = String(value);
  }
  return (text ?? String(value))
    .replace(/Bearer\s+[A-Za-z0-9._~-]+/gi, "Bearer [MASQUÉ]")
    .replace(/(password|motDePasse|access_token|refresh_token)(["'=: ]+)[^,\s}"']+/gi,
      "$1$2[MASQUÉ]")
    .slice(0, 4000);
};

export function addDiagnosticEvent(event: Omit<DiagnosticEvent, "at">) {
  const captured = { ...event, at: new Date().toISOString() };
  events.push(captured);
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
  return captured;
}

export function setDiagnosticContext(context: DiagnosticBusinessContext) {
  businessContext = {
    screen: context.screen.slice(0, 100),
    action: context.action?.slice(0, 150) ?? null,
    entities: context.entities.slice(0, 20).map((entity) => ({
      ...entity,
      identifier: entity.identifier.slice(0, 150),
      status: entity.status?.slice(0, 80) ?? null
    }))
  };
}

export function setDiagnosticScreen(screen: string) {
  const normalizedScreen = screen.slice(0, 100);
  if (businessContext.screen === normalizedScreen) return;
  businessContext = { screen: normalizedScreen, action: null, entities: [] };
}

export function installAutomaticDiagnosticReporter(
  reporter: (diagnostic: AutomaticDiagnostic) => Promise<void>
) {
  automaticReporter = reporter;
}

export function installDiagnosticCapture() {
  if (installed) return;
  installed = true;
  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    addDiagnosticEvent({ type: "console", message: args.map(redact).join(" ") });
    originalError(...args);
  };
  window.addEventListener("error", (event) => {
    const captured = addDiagnosticEvent({
      type: "javascript", message: redact(event.message),
      detail: `${event.filename || ""}:${event.lineno || 0}:${event.colno || 0}`
    });
    void reportAutomatically(captured, "CRITICAL");
  });
  window.addEventListener("unhandledrejection", (event) => {
    const captured = addDiagnosticEvent({
      type: "promise", message: redact(event.reason)
    });
    void reportAutomatically(captured, "CRITICAL");
  });
}

export function recordApiDiagnostic(path: string, status: number, detail?: string) {
  const captured = addDiagnosticEvent({
    type: "api", message: `${status} ${path}`, detail: redact(detail ?? "")
  });
  if ((status === 0 || status >= 500) && !path.includes("/terminal-diagnostics")) {
    void reportAutomatically(captured, status >= 503 ? "CRITICAL" : "ERROR");
  }
}

export function recordUnhandledDiagnostic(error: unknown, detail?: string) {
  const captured = addDiagnosticEvent({
    type: "javascript", message: redact(error), detail: redact(detail ?? "")
  });
  void reportAutomatically(captured, "CRITICAL");
}

export function terminalId() {
  const key = "obviowms.terminal-id";
  try {
    let id = localStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
    }
    return id;
  } catch {
    fallbackTerminalId ??= crypto.randomUUID();
    return fallbackTerminalId;
  }
}

export function diagnosticSnapshot() {
  const viewport = window.visualViewport;
  return {
    terminalId: terminalId(),
    events: [...events],
    businessContext: {
      ...businessContext,
      entities: businessContext.entities.map((entity) => ({ ...entity }))
    },
    technicalInfo: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: { width: screen.width, height: screen.height,
        availableWidth: screen.availWidth, availableHeight: screen.availHeight,
        colorDepth: screen.colorDepth, orientation: screen.orientation?.type ?? null },
      viewport: { innerWidth: window.innerWidth, innerHeight: window.innerHeight,
        visualWidth: viewport?.width ?? null, visualHeight: viewport?.height ?? null,
        scale: viewport?.scale ?? null },
      devicePixelRatio: window.devicePixelRatio,
      online: navigator.onLine,
      touchPoints: navigator.maxTouchPoints,
      capabilities: {
        camera: Boolean(navigator.mediaDevices?.getUserMedia),
        barcodeDetector: "BarcodeDetector" in window,
        localStorage: storageAvailable(),
        serviceWorker: "serviceWorker" in navigator
      },
      frontendVersion: import.meta.env.VITE_APP_VERSION ?? "1.0.0903b"
    }
  };
}

async function reportAutomatically(event: DiagnosticEvent, severity: DiagnosticSeverity) {
  if (!automaticReporter || automaticReportInProgress) return;
  const fingerprint = diagnosticFingerprint(event);
  const storageKey = `obviowms.diagnostic.${fingerprint}`;
  try {
    const lastReport = Number(sessionStorage.getItem(storageKey) ?? "0");
    if (Date.now() - lastReport < AUTOMATIC_REPORT_INTERVAL_MS) return;
  } catch {
    // Private browsing may prevent session storage; the in-memory guard remains active.
  }
  automaticReportInProgress = true;
  try {
    const snapshot = diagnosticSnapshot();
    await automaticReporter({ source: "AUTOMATIC", severity, fingerprint,
      businessContext: snapshot.businessContext, snapshot });
    try { sessionStorage.setItem(storageKey, String(Date.now())); } catch {
      // Reporting remains valid when session storage is unavailable.
    }
  } catch {
    // Diagnostic reporting must never alter the operational workflow.
  } finally {
    automaticReportInProgress = false;
  }
}

function diagnosticFingerprint(event: DiagnosticEvent) {
  const input = `${event.type}|${event.message}|${event.detail ?? ""}|${businessContext.screen}`;
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `ERR-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function storageAvailable() {
  try { localStorage.setItem("obviowms.storage-test", "1");
    localStorage.removeItem("obviowms.storage-test"); return true; }
  catch { return false; }
}
